import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus, FaTrash, FaEye, FaClock, FaInfinity } from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import { getToken } from '../../../../utils/auth';

function QuickViewSettings() {
  const { id: propertyId } = useParams();
  const location = useLocation();
  const propertyIdFromUrl = location.pathname.match(/\/propertydashboard\/(\d+)\/settings/)?.[1]
    || location.pathname.match(/\/properties\/(\d+)\/settings/)?.[1];
  const effectivePropertyId = (propertyId || propertyIdFromUrl) ? parseInt(propertyId || propertyIdFromUrl) : null;

  const [property, setProperty] = useState(null);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const buildHeaders = useCallback(() => {
    const t = getToken();
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  }, []);

  const fetchProperty = useCallback(async () => {
    if (!effectivePropertyId) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}`), { headers: buildHeaders() });
      if (res.ok) setProperty(await res.json());
    } catch (e) {
      console.error('Failed to fetch property:', e);
    }
  }, [effectivePropertyId, buildHeaders]);

  const fetchViewers = useCallback(async () => {
    if (!effectivePropertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${effectivePropertyId}/quick-viewers`),
        { headers: buildHeaders() }
      );
      if (res.ok) setViewers(await res.json());
    } catch (e) {
      console.error('Failed to fetch viewers:', e);
    } finally {
      setLoading(false);
    }
  }, [effectivePropertyId, buildHeaders]);

  useEffect(() => { fetchProperty(); }, [fetchProperty]);
  useEffect(() => { fetchViewers(); }, [fetchViewers]);

  const handleRevoke = async (linkId, name) => {
    if (!window.confirm(`Revoke view access for ${name}?`)) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}/quick-viewers/${linkId}`), {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (res.ok) {
        toast.success('Access revoked');
        fetchViewers();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || data.message || 'Failed to revoke', { autoClose: 4000 });
      }
    } catch (e) {
      toast.error('Network error', { autoClose: 4000 });
    }
  };

  const formatExpiry = (expiresAt, isExpired) => {
    if (!expiresAt) return { text: 'Permanent', icon: <FaInfinity style={{ color: '#059669' }} />, color: '#059669' };
    const date = new Date(expiresAt);
    const formatted = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (isExpired) return { text: `Expired ${formatted}`, icon: <FaClock style={{ color: '#dc2626' }} />, color: '#dc2626' };
    return { text: `Expires ${formatted}`, icon: <FaClock style={{ color: '#d97706' }} />, color: '#d97706' };
  };

  if (!effectivePropertyId) {
    return (
      <div className="stg-max-wide">
        <p className="stg-page-subtitle">Select a property to manage quick view access.</p>
      </div>
    );
  }

  return (
    <div className="stg-max-wide">
      <div className="stg-page-header">
        <h1 className="stg-page-title">Quick View</h1>
        <p className="stg-page-subtitle">
          Grant view-only access to this property using anyone&apos;s 6-digit CloudSafe ID.
          You can set an expiry date or grant permanent access.
        </p>
      </div>

      <div className="stg-property-info">
        <strong>Property:</strong> {property?.name || 'Loading...'}
      </div>

      <div className="stg-section-header">
        <h2 className="stg-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Viewers {viewers.length > 0 && <span className="stg-count">{viewers.length}</span>}
        </h2>
        <button className="stg-btn stg-btn-primary stg-btn-sm" onClick={() => setShowAddModal(true)}>
          <FaPlus /> Add Viewer
        </button>
      </div>

      {loading ? (
        <div className="stg-loading">Loading...</div>
      ) : viewers.length === 0 ? (
        <div className="stg-empty">
          <FaEye className="stg-empty-icon" />
          <p>No quick view access granted for this property yet.</p>
          <button className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add Viewer
          </button>
        </div>
      ) : (
        <div className="stg-entity-list">
          {viewers.map(v => {
            const expiry = formatExpiry(v.expires_at, v.is_expired);
            return (
              <div className="stg-entity-card" key={v.link_id} style={{ opacity: v.is_expired ? 0.6 : 1 }}>
                <div className="stg-entity-info">
                  <div className="stg-entity-avatar"><FaEye /></div>
                  <div>
                    <h3 className="stg-entity-name">
                      {v.first_name} {v.last_name}
                      {v.is_expired && <span style={{ fontSize: '0.7rem', color: '#dc2626', marginLeft: 8, fontWeight: 500 }}>EXPIRED</span>}
                    </h3>
                    <p className="stg-entity-detail">{v.email}</p>
                    <p className="stg-entity-detail" style={{ display: 'flex', alignItems: 'center', gap: 4, color: expiry.color }}>
                      {expiry.icon} {expiry.text}
                    </p>
                  </div>
                </div>
                <button
                  className="stg-remove-btn"
                  onClick={() => handleRevoke(v.link_id, `${v.first_name} ${v.last_name}`)}
                  title="Revoke access"
                >
                  <FaTrash />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddQuickViewModal
          propertyId={effectivePropertyId}
          propertyName={property?.name || ''}
          onClose={() => setShowAddModal(false)}
          onAdded={fetchViewers}
        />
      )}
    </div>
  );
}

function AddQuickViewModal({ propertyId, propertyName, onClose, onAdded }) {
  const [userCode, setUserCode] = useState('');
  const [step, setStep] = useState('enter');
  const [verified, setVerified] = useState(null);
  const [accessType, setAccessType] = useState('permanent');
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    const code = userCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('CloudSafe ID must be exactly 6 digits');
      return;
    }
    setError('');
    setVerified(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/quick-viewers/lookup?user_code=${encodeURIComponent(code)}`),
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.found) {
        setVerified(data);
        setStep('verify');
      } else {
        setError(data.message || 'No user found with this CloudSafe ID');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAdd = async (e) => {
    e.preventDefault();
    if (!verified) return;

    if (accessType === 'temporary' && !expiryDate) {
      setError('Please set an expiry date');
      return;
    }

    setSubmitting(true);
    setError('');

    let expires_at = null;
    if (accessType === 'temporary' && expiryDate) {
      expires_at = `${expiryDate}T${expiryTime || '23:59'}:00`;
    }

    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/quick-viewers`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ user_id: verified.user_id, expires_at })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`View access granted to ${verified.first_name} ${verified.last_name}`);
        onAdded();
        onClose();
      } else {
        setError(data.error || data.message || 'Failed to grant access');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Set default expiry date to tomorrow
  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate());
    return d.toISOString().split('T')[0];
  };

  return (
    <div className="stg-modal-overlay" onClick={onClose}>
      <div className="stg-modal" onClick={e => e.stopPropagation()}>
        <div className="stg-modal-header">
          <h3>{step === 'verify' ? 'Set access & confirm' : 'Add Quick Viewer'}</h3>
          <button type="button" className="stg-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={handleLookup}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Enter anyone&apos;s 6-digit CloudSafe ID to grant them view-only access to this property.
                They can find their ID in their account settings or ID badge.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">CloudSafe ID (6 digits) <span className="stg-required">*</span></label>
                <input
                  type="text"
                  className="stg-input"
                  placeholder="e.g. 123456"
                  value={userCode}
                  onChange={e => setUserCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Looking up\u2026' : 'Find User'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmAdd}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Verify the user and choose how long they can view this property.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-verify-box">
                <p className="stg-verify-label">Name</p>
                <p className="stg-verify-value">{verified?.first_name} {verified?.last_name}</p>
                <p className="stg-verify-label">Email</p>
                <p className="stg-verify-value">{verified?.email}</p>
                <p className="stg-verify-label">CloudSafe ID</p>
                <p className="stg-verify-value stg-verify-code">{verified?.user_code}</p>
              </div>

              <div className="stg-form-group" style={{ marginTop: 16 }}>
                <label className="stg-form-label">Access Duration</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    className={`stg-btn stg-btn-sm ${accessType === 'permanent' ? 'stg-btn-primary' : 'stg-btn-outline'}`}
                    onClick={() => setAccessType('permanent')}
                    style={{ flex: 1 }}
                  >
                    <FaInfinity style={{ marginRight: 4 }} /> Permanent
                  </button>
                  <button
                    type="button"
                    className={`stg-btn stg-btn-sm ${accessType === 'temporary' ? 'stg-btn-primary' : 'stg-btn-outline'}`}
                    onClick={() => setAccessType('temporary')}
                    style={{ flex: 1 }}
                  >
                    <FaClock style={{ marginRight: 4 }} /> Temporary
                  </button>
                </div>
              </div>

              {accessType === 'temporary' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <div className="stg-form-group" style={{ flex: 2 }}>
                    <label className="stg-form-label">Expiry Date <span className="stg-required">*</span></label>
                    <input
                      type="date"
                      className="stg-input"
                      value={expiryDate}
                      onChange={e => setExpiryDate(e.target.value)}
                      min={getMinDate()}
                    />
                  </div>
                  <div className="stg-form-group" style={{ flex: 1 }}>
                    <label className="stg-form-label">Time</label>
                    <input
                      type="time"
                      className="stg-input"
                      value={expiryTime}
                      onChange={e => setExpiryTime(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => { setStep('enter'); setVerified(null); setError(''); }}>Back</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Granting\u2026' : 'Grant View Access'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default QuickViewSettings;
